import {
  httpsCallable,
} from "firebase/functions";
import {
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref as storageRef,
  uploadBytesResumable,
} from "firebase/storage";
import { db, functions, storage } from "../../firebase";
import {
  MAX_IMAGE_SIZE_MB,
  MAX_VIDEO_SIZE_MB,
  MEDIA_UPLOAD_STATUS,
  SUPPORTED_IMAGE_EXTENSIONS,
  SUPPORTED_IMAGE_TYPES,
  SUPPORTED_VIDEO_EXTENSIONS,
  SUPPORTED_VIDEO_TYPES,
  TV_IMAGE_STORAGE,
  TV_MEDIA_TYPES,
  TV_VIDEO_STORAGE,
} from "./tvDisplayConstants";
import {
  getTvDisplayMediaCollectionRef,
  getTvDisplayDevicesCollectionRef,
  getTvDisplayDevicePublishedCurrentDocRef,
  getTvDisplayPlaylistItemsCollectionRef,
  initializeTvDisplayConfig,
} from "./tvDisplayService";

const callDeleteTvDisplayMedia = httpsCallable(functions, "deleteTvDisplayMedia");

export function subscribeToTvMedia(callback, errorCallback) {
  const q = query(getTvDisplayMediaCollectionRef(), orderBy("createdAt", "desc"));
  return onSnapshot(
    q,
    (snapshot) => {
      const rows = snapshot.docs.map((entry) => normalizeTvMedia(entry));
      if (typeof callback === "function") callback(rows);
    },
    (error) => {
      if (typeof errorCallback === "function") errorCallback(error);
    }
  );
}

export async function getTvMedia(mediaId) {
  if (!mediaId) throw new Error("Media id is required");
  const snapshot = await getDoc(doc(getTvDisplayMediaCollectionRef(), String(mediaId)));
  if (!snapshot.exists()) throw new Error("Media record not found");
  return normalizeTvMedia(snapshot);
}

export async function renameTvMedia(mediaId, name) {
  const cleanName = String(name || "").trim();
  if (!mediaId) throw new Error("Media id is required");
  if (!cleanName) throw new Error("Media name is required");
  if (cleanName.length > 120) throw new Error("Media name is too long");

  await updateDoc(doc(getTvDisplayMediaCollectionRef(), String(mediaId)), {
    name: cleanName,
    updatedAt: serverTimestamp(),
  });
}

export async function setTvMediaEnabled(mediaId, enabled) {
  if (!mediaId) throw new Error("Media id is required");
  await updateDoc(doc(getTvDisplayMediaCollectionRef(), String(mediaId)), {
    enabled: Boolean(enabled),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteTvMedia(mediaOrId) {
  const target =
    typeof mediaOrId === "string" ? await getTvMedia(mediaOrId) : mediaOrId;

  if (!target?.id) throw new Error("Media record not found");
  try {
    await callDeleteTvDisplayMedia({ mediaId: String(target.id) });
  } catch (error) {
    const code = String(error?.code || "").toLowerCase();
    if (!code.includes("internal") && !code.includes("unimplemented") && !code.includes("not-found")) {
      throw error;
    }
    await deleteTvMediaAsAdminFallback(target);
  }
}

async function deleteTvMediaAsAdminFallback(target) {
  const devicesSnapshot = await getDocs(getTvDisplayDevicesCollectionRef());
  const pointerSnapshots = await Promise.all(
    devicesSnapshot.docs.map((deviceSnapshot) =>
      getDoc(getTvDisplayDevicePublishedCurrentDocRef(deviceSnapshot.id))
    )
  );
  const currentPublicationIds = new Set(
    pointerSnapshots
      .filter((snapshot) => snapshot.exists())
      .map((snapshot) => String(snapshot.data()?.publicationId || ""))
      .filter(Boolean)
  );

  const referencesSnapshot = await getDocs(
    query(collectionGroup(db, "items"), where("mediaId", "==", String(target.id)))
  );
  const activePublicationReference = referencesSnapshot.docs.find((snapshot) => {
    const owner = snapshot.ref.parent.parent;
    return owner?.parent?.id === "publications" && currentPublicationIds.has(owner.id);
  });
  if (activePublicationReference) {
    throw new Error("This media is currently published to a TV. Publish a replacement playlist before deleting it.");
  }

  const playlistReferences = referencesSnapshot.docs.filter(
    (snapshot) => snapshot.ref.parent.parent?.parent?.id === "playlists"
  );
  const affectedPlaylistIds = [...new Set(
    playlistReferences.map((snapshot) => snapshot.ref.parent.parent?.id).filter(Boolean)
  )];

  for (let index = 0; index < playlistReferences.length; index += 400) {
    const batch = writeBatch(db);
    playlistReferences.slice(index, index + 400).forEach((snapshot) => batch.delete(snapshot.ref));
    // eslint-disable-next-line no-await-in-loop
    await batch.commit();
  }

  for (const playlistId of affectedPlaylistIds) {
    // eslint-disable-next-line no-await-in-loop
    const remainingItems = await getDocs(getTvDisplayPlaylistItemsCollectionRef(playlistId));
    // eslint-disable-next-line no-await-in-loop
    await updateDoc(doc(getTvDisplayMediaCollectionRef().parent, "playlists", playlistId), {
      itemCount: remainingItems.size,
      status: "DRAFT",
      hasUnpublishedChanges: true,
      updatedAt: serverTimestamp(),
    });
  }

  if (target.storagePath) {
    try {
      await deleteObject(storageRef(storage, target.storagePath));
    } catch (error) {
      if (!String(error?.code || "").toLowerCase().includes("object-not-found")) throw error;
    }
  }
  await deleteDoc(doc(getTvDisplayMediaCollectionRef(), String(target.id)));
}

export function uploadTvMedia({ file, onUpdate }) {
  if (!(file instanceof File)) throw new Error("A browser File is required");
  const validation = validateTvMediaFile(file);
  if (!validation.ok) {
    const error = new Error(validation.message);
    error.code = validation.code;
    throw error;
  }

  let uploadTask = null;

  const promise = (async () => {
    await initializeTvDisplayConfig();

    const mediaType = detectTvMediaType(file);
    const metadata = await extractTvMediaMetadata(file, mediaType);
    const docRef = doc(getTvDisplayMediaCollectionRef());
    const storagePath = buildTvMediaStoragePath(file, mediaType);
    const uploadRef = storageRef(storage, storagePath);

    onUpdate?.({
      status: MEDIA_UPLOAD_STATUS.UPLOADING,
      progress: 0,
      error: "",
      mediaId: docRef.id,
      storagePath,
    });

    const downloadURL = await new Promise((resolve, reject) => {
      uploadTask = uploadBytesResumable(uploadRef, file, {
        contentType: file.type || undefined,
      });

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = snapshot.totalBytes > 0
            ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
            : 0;
          onUpdate?.({
            status: MEDIA_UPLOAD_STATUS.UPLOADING,
            progress,
            error: "",
            mediaId: docRef.id,
            storagePath,
          });
        },
        (error) => {
          reject(error);
        },
        async () => {
          try {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
          } catch (error) {
            reject(error);
          }
        }
      );
    });

    const payload = {
      name: buildDefaultMediaName(file.name),
      originalName: file.name,
      type: mediaType,
      mimeType: String(file.type || ""),
      extension: getFileExtension(file.name),
      sizeBytes: Number(file.size || 0),
      storagePath,
      downloadURL,
      width: metadata.width,
      height: metadata.height,
      durationSeconds: metadata.durationSeconds,
      enabled: true,
      uploadStatus: MEDIA_UPLOAD_STATUS.READY,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    try {
      await setDoc(docRef, payload);
    } catch (error) {
      try {
        await deleteObject(uploadRef);
      } catch (cleanupError) {
        console.error("TV media upload cleanup failed:", cleanupError);
      }
      throw error;
    }

    onUpdate?.({
      status: MEDIA_UPLOAD_STATUS.COMPLETED,
      progress: 100,
      error: "",
      mediaId: docRef.id,
      storagePath,
    });

    return {
      id: docRef.id,
      storagePath,
      downloadURL,
      metadata: payload,
    };
  })().catch((error) => {
    const normalized = humanizeStorageError(error, "Unable to upload media.");
    const code = String(error?.code || "").toLowerCase();
    const cancelled =
      code.includes("canceled") ||
      code.includes("cancelled") ||
      normalized.message === "Upload cancelled.";

    onUpdate?.({
      status: cancelled ? MEDIA_UPLOAD_STATUS.CANCELLED : MEDIA_UPLOAD_STATUS.FAILED,
      progress: 0,
      error: normalized.message,
    });
    throw normalized;
  });

  return {
    cancel() {
      if (uploadTask) uploadTask.cancel();
    },
    promise,
  };
}

export function validateTvMediaFile(file) {
  const type = detectTvMediaType(file);
  if (!type) {
    return {
      ok: false,
      code: "unsupported-file",
      message: "Unsupported file type. Upload MP4, JPG, JPEG, PNG, or WEBP only.",
    };
  }

  const sizeMb = Number(file?.size || 0) / (1024 * 1024);
  const maxSize = type === TV_MEDIA_TYPES.VIDEO ? MAX_VIDEO_SIZE_MB : MAX_IMAGE_SIZE_MB;
  if (sizeMb > maxSize) {
    return {
      ok: false,
      code: "file-too-large",
      message: "File exceeds the allowed upload size.",
    };
  }

  return { ok: true, code: "", message: "" };
}

export function detectTvMediaType(file) {
  const mimeType = String(file?.type || "").toLowerCase();
  const extension = `.${getFileExtension(file?.name || "")}`;

  if (SUPPORTED_VIDEO_TYPES.includes(mimeType) || SUPPORTED_VIDEO_EXTENSIONS.includes(extension)) {
    return TV_MEDIA_TYPES.VIDEO;
  }
  if (SUPPORTED_IMAGE_TYPES.includes(mimeType) || SUPPORTED_IMAGE_EXTENSIONS.includes(extension)) {
    return TV_MEDIA_TYPES.IMAGE;
  }
  return "";
}

export function sanitizeFileName(name = "") {
  const base = String(name || "")
    .normalize("NFKD")
    .replace(/[^\x20-\x7E]/g, "")
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  return base || "media_file";
}

export function buildTvMediaStoragePath(file, mediaType) {
  const folder = mediaType === TV_MEDIA_TYPES.VIDEO ? TV_VIDEO_STORAGE : TV_IMAGE_STORAGE;
  const safeName = sanitizeFileName(file?.name || "media_file");
  const stamp = Date.now();
  const token = Math.random().toString(36).slice(2, 10);
  return `${folder}/${stamp}_${token}_${safeName}`;
}

export function buildDefaultMediaName(filename = "") {
  const raw = String(filename || "").replace(/\.[^.]+$/, "");
  return raw
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim() || "untitled media";
}

export function getFileExtension(filename = "") {
  const parts = String(filename || "").toLowerCase().split(".");
  return parts.length > 1 ? parts.pop() : "";
}

export function formatFileSize(bytes = 0) {
  const size = Number(bytes || 0);
  if (!Number.isFinite(size) || size <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let value = size;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value >= 10 || unitIndex === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`;
}

function normalizeTvMedia(snapshot) {
  const data = snapshot.data() || {};
  return {
    id: snapshot.id,
    ...data,
    name: String(data.name || ""),
    originalName: String(data.originalName || ""),
    type: String(data.type || ""),
    mimeType: String(data.mimeType || ""),
    extension: String(data.extension || ""),
    sizeBytes: Number(data.sizeBytes || 0),
    storagePath: String(data.storagePath || ""),
    downloadURL: String(data.downloadURL || ""),
    width: nullableNumber(data.width),
    height: nullableNumber(data.height),
    durationSeconds: nullableNumber(data.durationSeconds),
    enabled: data.enabled !== false,
    uploadStatus: String(data.uploadStatus || MEDIA_UPLOAD_STATUS.READY),
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
    createdAtMs: toMillis(data.createdAt),
    updatedAtMs: toMillis(data.updatedAt),
    searchText: `${data.name || ""} ${data.originalName || ""}`.toLowerCase(),
  };
}

async function extractTvMediaMetadata(file, mediaType) {
  try {
    return mediaType === TV_MEDIA_TYPES.VIDEO
      ? await readVideoMetadata(file)
      : await readImageMetadata(file);
  } catch (error) {
    console.warn("TV media metadata extraction failed:", error);
    return {
      width: null,
      height: null,
      durationSeconds: null,
    };
  }
}

function readImageMetadata(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      resolve({
        width: nullableNumber(image.naturalWidth),
        height: nullableNumber(image.naturalHeight),
        durationSeconds: null,
      });
      URL.revokeObjectURL(url);
    };
    image.onerror = (error) => {
      URL.revokeObjectURL(url);
      reject(error);
    };
    image.src = url;
  });
}

function readVideoMetadata(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.onloadedmetadata = () => {
      resolve({
        width: nullableNumber(video.videoWidth),
        height: nullableNumber(video.videoHeight),
        durationSeconds: nullableNumber(video.duration && Number(video.duration.toFixed(2))),
      });
      URL.revokeObjectURL(url);
    };
    video.onerror = (error) => {
      URL.revokeObjectURL(url);
      reject(error);
    };
    video.src = url;
  });
}

function humanizeStorageError(error, fallback) {
  const code = String(error?.code || "").toLowerCase();
  if (code.includes("canceled") || code.includes("cancelled")) {
    return new Error("Upload cancelled.");
  }
  if (code.includes("unauthorized") || code.includes("permission-denied")) {
    return new Error("You do not have permission to perform this media action.");
  }
  if (code.includes("retry-limit-exceeded")) {
    return new Error("The upload timed out. Please try again.");
  }
  if (code.includes("network-request-failed")) {
    return new Error("Network error while talking to Firebase Storage.");
  }
  return error instanceof Error ? new Error(error.message || fallback) : new Error(fallback);
}

function nullableNumber(value) {
  if (value == null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const stamp = new Date(value).getTime();
  return Number.isFinite(stamp) ? stamp : 0;
}
